import { HardDrive } from "lucide-react";
import { useState } from "react";
import { formatBytes, formatCount } from "@/shared/lib/format";
import { Skeleton } from "@/shared/ui";
import { useStorageUsage, type UsageScope } from "../api/usage";

/** Plain figures only: the backend has no quota, so no progress bar against an invented limit. */
export function StorageUsage({ isAdmin = false }: { isAdmin?: boolean }) {
  const [scope, setScope] = useState<UsageScope | undefined>(undefined);
  const usage = useStorageUsage(scope);

  if (usage.isPending) return <Skeleton className="h-20 rounded-xl" />;
  if (usage.isError) return <p className="text-xs text-muted">Usage unavailable</p>;

  const effective = usage.data.scope as UsageScope;
  const totalBytes = usage.data.buckets.reduce((n, b) => n + b.used_bytes, 0);
  const totalFiles = usage.data.buckets.reduce((n, b) => n + b.file_count, 0);
  return (
    <section
      aria-label="Storage used"
      className="rounded-xl border border-border bg-surface/80 p-3.5 shadow-2xs backdrop-blur-xs"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <HardDrive aria-hidden size={14} />
        </div>
        <div>
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted">
            {effective === "all" ? "Storage (everyone)" : "Storage"}
          </p>
        </div>
      </div>
      <p className="text-base font-semibold tracking-tight text-fg">{formatBytes(totalBytes)}</p>
      <p className="text-xs text-muted">{formatCount(totalFiles)} files</p>
      {isAdmin ? (
        <button
          type="button"
          className="mt-2 block cursor-pointer text-xs font-medium text-primary hover:underline"
          onClick={() => setScope(effective === "all" ? "mine" : "all")}
        >
          {effective === "all" ? "Show only my files" : "Show everyone's files"}
        </button>
      ) : null}
    </section>
  );
}
